import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// Upload (Import CSV) and Trash2 (Reset data) are hidden for now — see the header.
import {
  Clock,
  DoorClosed,
  DoorOpen,
  Eye,
  EyeOff,
  Handshake,
  Layers,
  ListPlus,
  PanelsTopLeft,
  Pause,
  Phone,
  PhoneCall,
  Play,
  Plus,
  Settings,
  Sparkles,
  Trash2,
  User,
  UserMinus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/lib/permissions';
import {
  AdminRequestError,
  moveLeads,
  clonePool,
  combineParsedFiles,
  createClient,
  createPool,
  defaultListName,
  deletePool,
  fetchBoard,
  fetchGlobalSpend,
  importContactRows,
  importThroxyList,
  parseThroxyCsv,
  revokeSdrPools,
  reassignPoolClient,
  renamePool,
  rowsFromCsv,
  setPoolHidden,
  setPoolOpenToAll,
  updatePool,
  wipeAllData,
  WIPE_CONFIRMATION,
  type AdminClient,
  type AdminPool,
  type BoardData,
  type BulkImportSummary,
  type CsvContactRow,
  type GlobalSpend,
  type PoolConfigPatch,
  type ThroxyImportResult,
  type ThroxyParsedRow,
} from './api';
import {
  checkThroxyClientId,
  formatSpend,
  liveSdrIds,
  poolReady,
  safeColor,
  buildSdrRoster,
  assignedRosterInPool,
  callingSdrsInPool,
  poolOpenBlockers,
  closedPoolDialable,
  validatePoolName,
  filterPools,
  sortPools,
  type SdrRosterEntry,
  type PoolStateFilter,
  type PoolSort,
} from './board-state';
import { PoolDetailsPanel } from './PoolDetailsPanel';
import { OpenAllPoolsButton } from './OpenAllPoolsButton';
import { Boundary } from '@/components/boundary';
import { ErrorState } from '@/components/error-state';
import { classify } from '@/lib/failure';

// =====================================================================
// Operator console board — a React port of dialer-console.html's Board tab.
// Left "All SDRs" rail, center pool columns, right "All contacts" rail, all
// grouped by pool. Drag an SDR/contact chip into a pool column to assign (POST
// move); ✕ unassigns a contact, the eye toggles hide, and "Open" boots a draft pool.
// Talks to the BFF's /bff/admin/* (admin/api.ts). Re-fetches the whole board
// after each mutation — the original's renderBoard() cadence.
// =====================================================================

// Background refresh cadence for the whole board. Every open console tab runs this
// against the (aggregate-heavy) GET /pools, so the cost scales with concurrent
// operators — 10s keeps the "calling now" chips live enough for an oversight board
// while halving that load vs 5s. SDRs' own real-time UI is the cockpit WS, not this.
const POLL_MS = 10000;

// A dragged client carries its SOURCE pool as well as the id: the same client sits on
// several cards, so the id alone cannot say which pool's leads are being dragged.
type DragPayload = ClientDrag;

type ClientDrag = {
  kind: 'client';
  id: number;
  name: string;
  fromPoolId: number;
  count: number;
  undialed: number;
};

// `title` / `pages` / `nav` / `signOut` are supplied by the route that mounts this board.
export function Board({
  title,
  pages,
  nav,
  signOut,
}: {
  title?: React.ReactNode;
  pages?: React.ReactNode;
  nav?: React.ReactNode;
  signOut?: React.ReactNode;
}) {
  const [data, setData] = useState<BoardData | null>(null);
  const [status, setStatus] = useState<string>('Loading…');
  const [error, setError] = useState<string | null>(null);
  // Surfaces create-form failures (client/pool/caller ID) inline — otherwise a
  // failed create is silent and "nothing happens" (e.g. the core's "no client
  // exists yet" 422). Cleared on the next successful-ish action.
  const [formError, setFormError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [throxyOpen, setThroxyOpen] = useState(false);
  // TEMPORARY — drives the deprecated "Fix pool clients" tool (FixPoolClientsModal);
  // remove once pools are natively multi-client.
  const [fixClientsOpen, setFixClientsOpen] = useState(false);
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  // Bumped by the toolbar Refresh so the cost widget reloads its own /spend fetch
  // alongside the board — the one Refresh button covers both. The background poll
  // does NOT bump this (spend stays out of the board poll to keep it lean).
  const [spendRefreshKey, setSpendRefreshKey] = useState(0);
  const [boardVersion, setBoardVersion] = useState(0);
  const canAdmin = useHasPermission('console:admin');
  const canOpenToAll = useHasPermission('pool-open-to-all:write');
  const dragRef = useRef<DragPayload | null>(null);
  // Toast stack — confirmation that outlives a modal (e.g. the throxy import).
  const [toasts, setToasts] = useState<BoardToast[]>([]);
  const toastSeq = useRef(0);
  const pushToast = useCallback((kind: BoardToast['kind'], text: string) => {
    const id = ++toastSeq.current;
    setToasts((cur) => [...cur, { id, kind, text }]);
    setTimeout(() => setToasts((cur) => cur.filter((t) => t.id !== id)), 6000);
  }, []);
  // Monotonic load id: the background poll and a mutation's reconcile reload can
  // be in flight at once, and `fetchBoard` is six parallel GETs whose total time
  // varies — so responses can land out of order. Without a guard a SLOW older
  // poll resolving after a FAST mutation reload would overwrite fresh state with
  // a stale snapshot (e.g. a just-unassigned contact flickering back / out for a
  // poll cycle). We stamp each load and only apply the latest one's result.
  const loadSeq = useRef(0);
  // True while a fetchBoard() is in flight. The periodic poll reads this to skip its
  // tick rather than stacking a second board load on top of a slow one — under many
  // concurrent operators GET /pools can run longer than POLL_MS, and piling requests
  // on only deepens the saturation that made it slow (the reported "worse with more
  // people on the console"). Explicit loads (mount, Refresh, post-mutation) ignore it.
  const inFlight = useRef(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setStatus('Loading…');
    const seq = ++loadSeq.current;
    inFlight.current = true;
    try {
      const fresh = await fetchBoard();
      if (seq !== loadSeq.current) return; // a newer load started — drop this stale result
      setData(fresh);
      setError(null);
      setStatus(
        `${fresh.pools.length} pool${fresh.pools.length === 1 ? '' : 's'} · updated ${new Date().toLocaleTimeString()}`,
      );
      // State, not the ref above: a refresh re-renders, clearing panels that
      // failed on the data it replaces.
      //
      // Only on an asked-for load. The background poll also lands here, and
      // bumping on that would clear a contained failure every few seconds
      // against data the server keeps returning — the panel would rethrow and
      // report itself on every tick, forever, flickering as it went.
      if (!quiet) setBoardVersion((n) => n + 1);
    } catch (err) {
      if (seq !== loadSeq.current) return; // superseded — let the newer load own the UI
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Load failed: ${msg}`);
      setStatus('Load failed');
    } finally {
      inFlight.current = false;
    }
  }, []);

  // Initial load + quiet poll (so "calling now" tracks cockpit joins). The poll
  // pauses while a drag is in flight so a re-render can't yank a chip mid-drag.
  useEffect(() => {
    void load();
    const t = setInterval(() => {
      if (dragRef.current) return;
      if (inFlight.current) return; // previous poll still running — skip, don't stack
      void load(true);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Run a mutation, surface its outcome, then reconcile against server truth.
  // `okMsg` toasts on success (mutations are otherwise silent until the reload
  // lands); an error without a handler toasts instead of vanishing.
  const run = useCallback(
    async (fn: () => Promise<unknown>, onErr?: (msg: string) => void, okMsg?: string) => {
      try {
        await fn();
        if (okMsg) pushToast('success', `✓ ${okMsg}`);
      } catch (err) {
        const msg = err instanceof AdminRequestError ? err.message : String(err);
        if (onErr) onErr(msg);
        else pushToast('error', msg);
      } finally {
        await load(true);
      }
    },
    [load, pushToast],
  );

  // Like `run`, but RETURNS the error instead of pushing it to the board banner: a
  // rename's 422 is about the field the operator is looking at, so the caller renders
  // it inline and stays open on the input rather than closing over a banner.
  const renamePoolAction = useCallback(
    async (id: number, name: string): Promise<string | null> => {
      try {
        await renamePool(id, name);
        pushToast('success', `✓ Renamed to "${name}"`);
        return null;
      } catch (err) {
        return err instanceof AdminRequestError ? err.message : String(err);
      } finally {
        await load(true);
      }
    },
    [load, pushToast],
  );

  // Apply "Redial no-pickups" on a closed pool. Like `renamePoolAction`, the error
  // comes BACK to the modal instead of the board banner — the core's 422 ("pool is
  // open, not closed") is about the pool the operator is looking at, and the modal
  // stays open on it. On success the applied result is handed back so the modal can
  // report exactly how many memberships moved, plus a toast that outlives the modal.
  // Same contract, for the pool's dialing settings: they are edited inside the details
  // panel, which covers the banner the board would otherwise put the failure in.
  const savePoolAction = useCallback(
    async (id: number, patch: PoolConfigPatch): Promise<string | null> => {
      try {
        await updatePool(id, patch);
        pushToast('success', '✓ Pool updated');
        return null;
      } catch (err) {
        return err instanceof AdminRequestError ? err.message : String(err);
      } finally {
        await load(true);
      }
    },
    [load, pushToast],
  );

  const doRevokeSdr = useCallback(
    (entry: SdrRosterEntry) => {
      const { sdrId, name, pool_ids } = entry;
      if (sdrId == null) return;

      const held = pool_ids.length;
      const scope =
        held === 0
          ? 'the pool they are on now'
          : held === 1
            ? 'their one pool'
            : `all ${held} of their pools`;
      if (!confirm(`Remove ${name} from ${scope}? A live call finishes first.`)) return;

      void run(async () => {
        const { revoked_pool_ids, pushed_out, draining } = await revokeSdrPools(sdrId);
        // Assignments and the seat are separate removals and either can be empty, so
        // each is named rather than reduced to one count that reads as "nothing done".
        const cleared =
          revoked_pool_ids.length > 0
            ? `${revoked_pool_ids.length} pool${revoked_pool_ids.length === 1 ? '' : 's'}`
            : pushed_out > 0
              ? 'the pool they were on'
              : 'nothing — they held no pools and no seat';
        const tail = draining > 0 ? ' — a call is still finishing' : '';
        pushToast('success', `✓ Removed ${name} from ${cleared}${tail}`);
      });
    },
    [pushToast, run],
  );

  // Two tiers: who you are, where you are and the way out on top — none of them actions
  // on this board — then what the board is doing and what you can do to it. The caller-ID
  // warning is a property of the number inventory every pool dials from, so it belongs
  // here once rather than repeated on every card.
  const header = (
    <div className="mb-5 flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-2">
        {title}
        <div className="flex flex-wrap items-center gap-3">
          {pages}
          {signOut}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <p
            className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}
            role="status"
            aria-live="polite"
          >
            {status}
          </p>
          {data && !poolReady(data.numbers) && (
            <span
              className="bg-destructive/10 text-destructive rounded-full px-2 py-0.5 text-[0.65rem] font-bold"
              title="No caller ID is dialable right now, so no pool can place a call. Buy or unflag numbers in Settings → Numbers."
            >
              ▲ no active caller ID
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Hidden for now — Import CSV. Restore when the bulk-import flow is wanted again. */}
          {/* <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
          <Upload className="mr-1 h-3.5 w-3.5" />
          Import CSV
        </Button> */}
          {/* One menu because the two are a sequence, not a choice: creating a pool
            imports a throxy list, and that can only file leads under a client which
            already has a Throxy ID — which nothing but Create client mints.
            FixPoolClientsModal below stays wired for a one-line restore. */}
          {canAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus />
                  Create
                </Button>
              </DropdownMenuTrigger>
              {/* Focus is left to return to the trigger: neither modal below claims it on
                  mount, so intercepting the hand-back would strand it on the document. */}
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setCreateClientOpen(true)}>
                  <Handshake />
                  Client
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setThroxyOpen(true)}>
                  <ListPlus />
                  Pool
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* Hidden for now — Reset data (destructive wipe). Restore if needed for testing. */}
          {/* <Button
          variant="outline"
          size="sm"
          onClick={() => setWipeOpen(true)}
          className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          title="Delete ALL dialer data (disabled in production)"
        >
          <Trash2 className="mr-1 h-3.5 w-3.5" />
          Reset data
        </Button> */}
          {nav}
          {canOpenToAll && (
            <OpenAllPoolsButton pools={data?.pools} onDone={() => void load(true)} />
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void load();
              setSpendRefreshKey((k) => k + 1);
            }}
          >
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );

  if (error && !data) {
    return (
      <div>
        {header}
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm">
          <p className="text-destructive font-medium">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // The stats degrade as a block, so any pool answers for all of them.
  const analyticsDown = (data?.pools ?? []).some((p) => p.total_dials === null);

  return (
    <div>
      {header}

      {analyticsDown && (
        <p className="bg-muted/40 text-muted-foreground mb-4 rounded-md px-3 py-2 text-sm">
          Analytics unavailable — pool stats, outcomes, activity and recordings can&rsquo;t be read
          right now. Everything else works normally.
        </p>
      )}

      {canAdmin && (
        <Boundary where="region:board-spend" resetKey={spendRefreshKey} size="panel">
          <GlobalCostWidget refreshKey={spendRefreshKey} />
        </Boundary>
      )}

      {importOpen && (
        <ModalBoundary where="region:board-bulk-import" onClose={() => setImportOpen(false)}>
          <BulkImportModal
            clients={data?.clients ?? []}
            onClose={() => setImportOpen(false)}
            onDone={() => void load(true)}
          />
        </ModalBoundary>
      )}

      {throxyOpen && (
        <ModalBoundary where="region:board-throxy-import" onClose={() => setThroxyOpen(false)}>
          <ThroxyImportModal
            clients={data?.clients ?? []}
            onClose={() => setThroxyOpen(false)}
            onDone={() => void load(true)}
            onToast={pushToast}
          />
        </ModalBoundary>
      )}

      {fixClientsOpen && (
        <ModalBoundary where="region:board-fix-clients" onClose={() => setFixClientsOpen(false)}>
          <FixPoolClientsModal
            pools={data?.pools ?? []}
            clients={data?.clients ?? []}
            onClose={() => setFixClientsOpen(false)}
            onDone={() => void load(true)}
          />
        </ModalBoundary>
      )}

      {createClientOpen && (
        <ModalBoundary
          where="region:board-create-client"
          onClose={() => setCreateClientOpen(false)}
        >
          <CreateClientModal
            clients={data?.clients ?? []}
            onClose={() => setCreateClientOpen(false)}
            onDone={() => void load(true)}
          />
        </ModalBoundary>
      )}

      {wipeOpen && (
        <ModalBoundary where="region:board-wipe-data" onClose={() => setWipeOpen(false)}>
          <WipeDataModal
            onClose={() => setWipeOpen(false)}
            onDone={() => {
              setWipeOpen(false);
              void load(true);
            }}
          />
        </ModalBoundary>
      )}

      {formError && (
        <div className="border-destructive/40 bg-destructive/5 mb-4 rounded-md border p-3 text-sm">
          <span className="text-destructive font-medium">⚠ {formError}</span>
        </div>
      )}

      {data && (
        <div className="flex items-start gap-4">
          <Boundary where="region:board-sdr-rail" resetKey={boardVersion} size="panel">
            <SdrRail data={data} onRevoke={doRevokeSdr} />
          </Boundary>

          {/* An outer one as well as the per-column boundaries inside, because
              the roster, filter and sort run in this component's own body —
              above the boundaries it renders, and so out of their reach. A
              malformed pool that breaks those takes the pools area; without
              this it takes the board. */}
          <Boundary where="region:board-pool-columns" resetKey={boardVersion} size="panel">
            <PoolColumns
              data={data}
              version={boardVersion}
              dragRef={dragRef}
              onDeletePool={(id, name) => {
                if (confirm(`Delete pool "${name}" permanently? This also deletes its contacts.`))
                  void run(() => deletePool(id), undefined, `Deleted "${name}"`);
              }}
              onClonePool={(id) =>
                run(
                  () => clonePool(id),
                  (msg) => setFormError(`Couldn't clone pool: ${msg}`),
                  'Pool cloned',
                )
              }
              onToggleHidden={(id, hidden) => run(() => setPoolHidden(id, hidden))}
              onToggleOpenToAll={(id, open) => run(() => setPoolOpenToAll(id, open))}
              onUpdatePool={(id, patch) =>
                run(
                  () => updatePool(id, patch),
                  (msg) => setFormError(`Couldn't save pool settings: ${msg}`),
                  'Pool updated',
                )
              }
              onRenamePool={renamePoolAction}
              onSavePool={savePoolAction}
              onChanged={() => void load(true)}
            />
          </Boundary>
        </div>
      )}

      {toasts.length > 0 && (
        <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-80 flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              className={cn(
                'rounded-md border px-3 py-2 text-sm shadow-lg',
                t.kind === 'success'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                  : 'border-destructive/40 bg-destructive/10 text-destructive',
              )}
            >
              {t.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface BoardToast {
  id: number;
  kind: 'success' | 'error';
  text: string;
}

// ---------------------------------------------------------------------------
// Global cost widget — total Twilio telephony + LLM disposition spend across all
// pools, for today, the current week or the current month. Fetches its own /spend
// figures on mount (independent of the board poll) so it never bloats the 6-GET board
// refresh. /spend returns all three windows in one response, so switching windows is
// pure client state and never refetches.
// Reuses the per-pool spend badge design (Phone / Sparkles icons + formatSpend) at a
// larger scale. Windows and boundaries are UTC — see AdminController.global_spend.
// ---------------------------------------------------------------------------
type SpendWin = 'today' | 'week' | 'month';

const SPEND_WINDOWS: ReadonlyArray<{ key: SpendWin; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
];

// A dialog that cannot draw must still be closable, or its overlay traps the
// board. The default fallback would render inside the dead dialog.
function ModalBoundary({
  where,
  onClose,
  children,
}: {
  where: `region:${string}`;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <Boundary
      where={where}
      resetKey={where}
      fallback={(error, reset) => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-card w-[min(28rem,94vw)] rounded-lg border p-6 shadow-xl">
            <ErrorState failure={classify(error)} size="panel" onRetry={reset}>
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </ErrorState>
          </div>
        </div>
      )}
    >
      {children}
    </Boundary>
  );
}

// `refreshKey` is bumped by the board's single toolbar Refresh so this widget's
// independent /spend fetch reloads alongside the board — no second Refresh button.
function GlobalCostWidget({ refreshKey }: { refreshKey: number }) {
  const [spend, setSpend] = useState<GlobalSpend | null>(null);
  const [win, setWin] = useState<SpendWin>('week');
  const [loading, setLoading] = useState(true);
  // The message, not a flag: "couldn't load" alone leaves an operator unable to tell a
  // broken widget from a store that is down.
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSpend(await fetchGlobalSpend());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t load spend');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const sel = spend?.[win];
  const twilio = sel?.twilio_micros ?? 0;
  const llm = sel?.llm_micros ?? 0;
  const calls = sel?.call_count ?? 0;
  const pools = sel?.pool_count ?? 0;

  return (
    <div className="mb-5 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Segmented today/week/month toggle — no shared segmented-control exists, so
              this mirrors the toolbar's paired-Button pattern (selected = default variant). */}
          <div className="inline-flex overflow-hidden rounded-md border">
            {SPEND_WINDOWS.map(({ key, label }, i) => (
              <button
                key={key}
                type="button"
                onClick={() => setWin(key)}
                className={cn(
                  'px-3 py-1 text-xs font-medium transition-colors',
                  i > 0 && 'border-l',
                  win === key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted',
                )}
                aria-pressed={win === key}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error ? (
        <p className="text-destructive mt-3 text-sm">{error}. Try Refresh.</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-x-8 gap-y-2">
          <span
            className="text-muted-foreground inline-flex items-center gap-1.5 text-sm tabular-nums"
            title="Estimated Twilio telephony cost across all pools (from call duration × list rates)"
          >
            <Phone className="h-4 w-4" />
            <b className="text-foreground text-base">{formatSpend(twilio)}</b> Telephony
          </span>
          <span
            className="text-muted-foreground inline-flex items-center gap-1.5 text-sm tabular-nums"
            title="LLM cost for AI call dispositions across all pools (at list prices)"
          >
            <Sparkles className="h-4 w-4" />
            <b className="text-foreground text-base">{formatSpend(llm)}</b> LLM
          </span>
          <span
            className="inline-flex items-center gap-1.5 text-sm tabular-nums"
            title="Combined telephony + LLM spend for the selected window"
          >
            Total <b className="text-foreground text-base">{formatSpend(twilio + llm)}</b>
          </span>
          <span
            className="text-muted-foreground inline-flex items-center gap-1.5 text-sm tabular-nums"
            title="Number of calls placed across all pools for the selected window"
          >
            <PhoneCall className="h-4 w-4" />
            <b className="text-foreground text-base">{calls.toLocaleString()}</b> Calls
          </span>
          <span
            className="text-muted-foreground inline-flex items-center gap-1.5 text-sm tabular-nums"
            title="Number of pools created for the selected window"
          >
            <Layers className="h-4 w-4" />
            <b className="text-foreground text-base">{pools.toLocaleString()}</b> Pools
          </span>
          {loading && <span className="text-muted-foreground text-xs">Loading…</span>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bulk CSV import — upload a `full_name,phone_number,client` file, preview the
// parsed rows, then create one contact per row (auto-creating any new client by
// name). No core bulk endpoint yet, so importContactRows fans out the POSTs
// (admin/api.ts). Lightweight overlay, same shape as PoolSettingsModal.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// WipeDataModal — DESTRUCTIVE full data reset, behind type-to-confirm + the
// button (double confirm). Truncates all dialer + event data (keeps auth). The
// core ALSO refuses in production (403), so this is a dev/canary tool; the prod
// error surfaces inline if someone tries.
// ---------------------------------------------------------------------------
function WipeDataModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const armed = typed.trim() === WIPE_CONFIRMATION;

  async function confirm() {
    if (!armed || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await wipeAllData();
      onDone();
    } catch (e) {
      setErr(e instanceof AdminRequestError ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card flex w-[min(32rem,94vw)] flex-col gap-4 rounded-lg border p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-destructive text-base font-bold">⚠ Reset all data</h2>
        <p className="text-sm">
          This permanently deletes <b>all clients, pools, contacts, SDRs, caller IDs</b> and the
          call/event history. Login accounts are kept. <b>This cannot be undone.</b>
        </p>
        <p className="text-muted-foreground text-xs">
          Disabled in production — the server refuses the wipe there. Active dialing sessions should
          be restarted afterwards.
        </p>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            Type <code className="bg-muted rounded px-1">{WIPE_CONFIRMATION}</code> to confirm
          </span>
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="rounded border px-2 py-1 text-sm"
            placeholder={WIPE_CONFIRMATION}
          />
        </label>
        {err && <p className="text-destructive text-sm">⚠ {err}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!armed || busy}
            onClick={() => void confirm()}
            className="bg-destructive hover:bg-destructive/90 text-white"
          >
            {busy ? 'Wiping…' : 'Delete everything'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BulkImportModal({
  clients,
  onClose,
  onDone,
}: {
  clients: AdminClient[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<CsvContactRow[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<BulkImportSummary | null>(null);

  // Which client names in the parsed rows don't exist yet — they'll be created.
  const known = new Set(clients.map((c) => c.name.trim().toLowerCase()));
  const newClients = rows
    ? [...new Set(rows.map((r) => r.client.trim()).filter((c) => c && !known.has(c.toLowerCase())))]
    : [];

  function onPick(file: File | undefined) {
    setSummary(null);
    setParseError(null);
    setRows(null);
    setFileName(file?.name ?? null);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        setRows(rowsFromCsv(String(reader.result ?? '')));
      } catch (err) {
        setParseError(err instanceof Error ? err.message : String(err));
      }
    };
    reader.onerror = () => setParseError("Couldn't read the file.");
    reader.readAsText(file);
  }

  async function runImport() {
    if (!rows) return;
    setImporting(true);
    try {
      const result = await importContactRows(rows, clients);
      setSummary(result);
      onDone(); // refresh the board so new contacts/clients show up
    } finally {
      setImporting(false);
    }
  }

  const failures = summary?.results.filter((r) => !r.ok) ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card flex max-h-[85vh] w-[min(40rem,94vw)] flex-col gap-4 overflow-y-auto rounded-lg border p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold">Import contacts from CSV</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg"
            title="Close"
          >
            ✕
          </button>
        </div>

        {!summary && (
          <>
            <p className="text-muted-foreground text-xs">
              Upload a CSV with the header{' '}
              <code className="bg-muted rounded px-1 py-0.5 font-mono">
                full_name,phone_number,client
              </code>
              . Phone numbers should be E.164 (e.g. <span className="font-mono">+1212555…</span>). A{' '}
              <b>client</b> that doesn&apos;t exist yet is created automatically.
            </p>

            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">CSV file</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => onPick(e.target.files?.[0])}
                className="text-sm file:mr-3 file:rounded-md file:border file:bg-muted file:px-3 file:py-1.5 file:text-sm"
              />
              {fileName && <span className="text-muted-foreground text-xs">{fileName}</span>}
            </label>

            {parseError && <p className="text-destructive text-xs">⚠ {parseError}</p>}

            {rows && (
              <div className="flex flex-col gap-2">
                <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                  <span className="bg-muted rounded-full px-2 py-0.5 font-bold">
                    {rows.length} row{rows.length === 1 ? '' : 's'}
                  </span>
                  {newClients.length > 0 && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-800">
                      {newClients.length} new client{newClients.length === 1 ? '' : 's'} will be
                      created
                    </span>
                  )}
                </div>
                {newClients.length > 0 && (
                  <p className="text-muted-foreground text-xs">
                    New clients: {newClients.join(', ')}
                  </p>
                )}
                <div className="max-h-64 overflow-y-auto rounded-md border">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                      <tr>
                        <th className="px-2 py-1 font-medium">full_name</th>
                        <th className="px-2 py-1 font-medium">phone_number</th>
                        <th className="px-2 py-1 font-medium">client</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 200).map((r, i) => {
                        const incomplete = !r.fullName || !r.phoneNumber || !r.client;
                        return (
                          <tr
                            key={i}
                            className={cn('border-t', incomplete && 'bg-destructive/5')}
                            title={incomplete ? 'Missing a required field — will be skipped' : ''}
                          >
                            <td className="px-2 py-1">{r.fullName || '—'}</td>
                            <td className="px-2 py-1 font-mono">{r.phoneNumber || '—'}</td>
                            <td className="px-2 py-1">{r.client || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {rows.length > 200 && (
                    <p className="text-muted-foreground px-2 py-1 text-[0.68rem]">
                      …and {rows.length - 200} more (all will be imported).
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!rows || rows.length === 0 || importing}
                onClick={runImport}
              >
                {importing
                  ? 'Importing…'
                  : `Import ${rows?.length ?? 0} contact${rows?.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </>
        )}

        {summary && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800">
                {summary.imported} imported
              </span>
              {summary.failed > 0 && (
                <span className="bg-destructive/10 text-destructive rounded-full px-2 py-0.5 font-bold">
                  {summary.failed} failed
                </span>
              )}
              {summary.clientsCreated.length > 0 && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-800">
                  {summary.clientsCreated.length} client
                  {summary.clientsCreated.length === 1 ? '' : 's'} created
                </span>
              )}
            </div>

            {failures.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-md border">
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                    <tr>
                      <th className="px-2 py-1 font-medium">row</th>
                      <th className="px-2 py-1 font-medium">full_name</th>
                      <th className="px-2 py-1 font-medium">reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failures.map((r) => (
                      <tr key={r.line} className="border-t">
                        <td className="px-2 py-1 font-mono">{r.line}</td>
                        <td className="px-2 py-1">{r.fullName || '—'}</td>
                        <td className="text-destructive px-2 py-1">{r.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end border-t pt-3">
              <Button size="sm" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Throxy list import — upload a rich throxy lead export, preview the parsed
// leads, name the list, then create a brand-new pool + campaign client + contacts
// (with rich metadata, in ranked dial order) in ONE call to /pools/import. The
// rich fields (company/title/persona/LinkedIn/location/email) ride along on the
// contact and surface on the SDR cockpit card when a human answers.
// ---------------------------------------------------------------------------
/**
 * Temporary admin tool: retroactively move a pool onto a client that has a Throxy
 * id. A pool has no client column — its client is the majority client across its
 * contacts (pool.effective_client), and reassigning rewrites contacts.client_id.
 * Pools on an id-less client are flagged and sorted first. Intentionally NOT on the
 * main board cards.
 *
 * @deprecated Temporary. Delete this modal, its toolbar button, and the
 * `reassignPoolClient`/`effective_client` plumbing once pools are natively
 * multi-client — a pool no longer maps to one client then, so the tool is moot.
 */
function FixPoolClientsModal({
  pools,
  clients,
  onClose,
  onDone,
}: {
  pools: AdminPool[];
  clients: AdminClient[];
  onClose: () => void;
  onDone: () => void;
}) {
  const eligibleClients = clients.filter((c) => c.throxy_client_id);
  const [selected, setSelected] = useState<Record<number, number | ''>>({});
  const [busyPoolId, setBusyPoolId] = useState<number | null>(null);
  const [rowState, setRowState] = useState<
    Record<number, { moved: number; skipped: number } | { error: string } | undefined>
  >({});

  // Pools needing attention (majority client has no Throxy id) first, then the
  // rest; stable by name within each group.
  const needsFix = (p: AdminPool) => !!p.effective_client && !p.effective_client.throxy_client_id;
  const ordered = [...pools].sort(
    (a, b) => Number(needsFix(b)) - Number(needsFix(a)) || a.name.localeCompare(b.name),
  );

  async function apply(pool: AdminPool) {
    const clientId = selected[pool.id];
    if (clientId === '' || clientId == null) return;
    setBusyPoolId(pool.id);
    setRowState((s) => ({ ...s, [pool.id]: undefined }));
    try {
      const res = await reassignPoolClient(pool.id, Number(clientId));
      setRowState((s) => ({ ...s, [pool.id]: { moved: res.moved, skipped: res.skipped } }));
      onDone(); // refetch the board so effective_client labels refresh
    } catch (err) {
      setRowState((s) => ({
        ...s,
        [pool.id]: { error: err instanceof AdminRequestError ? err.message : String(err) },
      }));
    } finally {
      setBusyPoolId(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card flex max-h-[85vh] w-[min(48rem,94vw)] flex-col gap-4 overflow-y-auto rounded-lg border p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold">Fix pool clients</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg"
            title="Close"
          >
            ✕
          </button>
        </div>

        <p className="text-muted-foreground text-xs">
          Reassign a pool onto a client that has a <b>Throxy ID</b>. A pool has no client of its own
          — this rewrites <code className="bg-muted rounded px-1 py-0.5 font-mono">client_id</code>{' '}
          on the pool&apos;s contacts, so a contact shared with other pools moves in <b>all</b> of
          them. Contacts whose phone already exists under the target client are <b>skipped</b>.
          Pools filed under an id-less client are flagged ⚠ and listed first.
        </p>

        {eligibleClients.length === 0 ? (
          <span className="text-destructive text-xs">
            No client has a Throxy ID yet. Create one with <b>Create client</b> first.
          </span>
        ) : ordered.length === 0 ? (
          <span className="text-muted-foreground text-xs">No pools.</span>
        ) : (
          <div className="flex flex-col divide-y">
            {ordered.map((pool) => {
              const ec = pool.effective_client;
              const result = rowState[pool.id];
              return (
                <div key={pool.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <div className="min-w-[10rem] flex-1">
                    <div className="font-medium">{pool.name}</div>
                    <div
                      className={cn(
                        'text-xs',
                        needsFix(pool) ? 'text-destructive' : 'text-muted-foreground',
                      )}
                    >
                      {!ec
                        ? '— no contacts'
                        : ec.throxy_client_id
                          ? `${ec.name} (${ec.throxy_client_id})`
                          : `⚠ ${ec.name} — no Throxy ID`}
                    </div>
                  </div>
                  <select
                    value={selected[pool.id] ?? ''}
                    onChange={(e) =>
                      setSelected((s) => ({
                        ...s,
                        [pool.id]: e.target.value ? Number(e.target.value) : '',
                      }))
                    }
                    className="border-input rounded-md border px-2 py-1.5 text-sm"
                  >
                    <option value="">Reassign to…</option>
                    {eligibleClients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.throxy_client_id})
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busyPoolId === pool.id || !selected[pool.id]}
                    onClick={() => void apply(pool)}
                  >
                    {busyPoolId === pool.id ? 'Applying…' : 'Apply'}
                  </Button>
                  <span className="min-w-[8rem] text-xs">
                    {result && 'error' in result ? (
                      <span className="text-destructive">⚠ {result.error}</span>
                    ) : result ? (
                      <span className="text-muted-foreground">
                        Moved {result.moved}, skipped {result.skipped}
                      </span>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// One uploaded export in the import modal: its parsed rows plus the client its
// leads are filed under (picked per file — a pool can span several clients).
interface ImportFile {
  key: number;
  fileName: string;
  rows: ThroxyParsedRow[] | null; // null while the file is still being read
  parseError: string | null;
  clientId: number | null;
  // What the operator typed in the client box; clientId is null until it names a client.
  clientQuery: string;
}

// Stay under the core's 32 MB request-body cap with headroom to spare.
const IMPORT_PAYLOAD_LIMIT_BYTES = 28 * 1024 * 1024;

function ThroxyImportModal({
  clients,
  onClose,
  onDone,
  onToast,
}: {
  clients: AdminClient[];
  onClose: () => void;
  onDone: () => void;
  onToast: (kind: 'success' | 'error', text: string) => void;
}) {
  const [files, setFiles] = useState<ImportFile[]>([]);
  const fileKey = useRef(0);
  const [listName, setListName] = useState('');
  // A hand-edited pool name is never overwritten by the suggestions below.
  const [nameTouched, setNameTouched] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [result, setResult] = useState<ThroxyImportResult | null>(null);
  // Only clients with a Throxy id are valid import targets — filing leads under an
  // id-less client would break leadflow sync (the whole point of this flow).
  const eligibleClients = useMemo(
    () =>
      clients
        .filter((c) => c.throxy_client_id)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [clients],
  );
  // Link contacts that already exist (same client + phone) to the new pool instead
  // of failing on the per-client unique-phone constraint — the "re-import" escape.
  const [reuseExisting, setReuseExisting] = useState(false);

  const clientName = (id: number | null) =>
    id == null ? null : (eligibleClients.find((c) => c.id === id)?.name ?? null);

  // Merge every parsed file (round-robin, cross-client duplicates screened).
  // Files without a client yet get a distinct placeholder id so the preview
  // behaves; the import button stays disabled until every file has a real one.
  const combined = useMemo(
    () =>
      combineParsedFiles(
        files
          .filter((f) => f.rows !== null)
          .map((f) => {
            const name = eligibleClients.find((c) => c.id === f.clientId)?.name ?? null;
            return {
              fileName: f.fileName,
              clientId: f.clientId ?? -(f.key + 1),
              rows: f.rows ?? [],
              ...(name ? { clientName: name } : {}),
            };
          }),
      ),
    [files, eligibleClients],
  );
  const importable = combined.importable;
  const skipped = combined.rows.length - importable.length;
  const allAssigned = files.length > 0 && files.every((f) => f.rows !== null && f.clientId != null);
  // No files at all → the modal doubles as a name-only "create empty pool" form
  // (the onboarding flow: pool shell first, lists imported and moved in later).
  const emptyCreate = files.length === 0;

  const perClientCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const contact of importable) {
      counts.set(contact.client_id, (counts.get(contact.client_id) ?? 0) + 1);
    }
    return [...counts.entries()].filter(([id]) => id > 0);
  }, [importable]);

  const distinctClientCount = useMemo(
    () => new Set(files.map((f) => f.clientId).filter((id): id is number => id != null)).size,
    [files],
  );

  const payloadBytes = useMemo(
    () => (allAssigned ? JSON.stringify(importable).length : 0),
    [allAssigned, importable],
  );
  const payloadTooBig = payloadBytes > IMPORT_PAYLOAD_LIMIT_BYTES;

  // All failures were duplicate-phone rejections AND reuse was off → the toggle
  // would fix it; surface a one-click hint instead of a wall of red.
  const allDuplicates =
    !!result &&
    result.failed > 0 &&
    result.failures.every((f) => /already been taken/i.test(f.error));

  // Pool-name suggestion: one file → the file name (as before); several files →
  // the distinct client names ("acme-beta").
  useEffect(() => {
    if (nameTouched) return;
    if (files.length === 1 && files[0]) {
      setListName(defaultListName(files[0].fileName));
      return;
    }
    const names = files
      .map((f) => clientName(f.clientId))
      .filter((n): n is string => n !== null)
      .map((n) => n.toLowerCase().replace(/\s+/g, '-'));
    const distinct = [...new Set(names)];
    if (distinct.length > 0) setListName(distinct.join('-'));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clientName only reads eligibleClients
  }, [files, nameTouched, eligibleClients]);

  function addFile(file: File | undefined) {
    if (!file) return;
    setResult(null);
    setImportError(null);
    const key = fileKey.current++;
    setFiles((prev) => [
      ...prev,
      { key, fileName: file.name, rows: null, parseError: null, clientId: null, clientQuery: '' },
    ]);
    const patch = (update: Partial<ImportFile>) =>
      setFiles((prev) => prev.map((f) => (f.key === key ? { ...f, ...update } : f)));
    const reader = new FileReader();
    reader.onload = () => {
      try {
        patch({ rows: parseThroxyCsv(String(reader.result ?? '')).rows });
      } catch (err) {
        patch({ parseError: err instanceof Error ? err.message : String(err) });
      }
    };
    reader.onerror = () => patch({ parseError: "Couldn't read the file." });
    reader.readAsText(file);
  }

  function removeFile(key: number) {
    setResult(null);
    setImportError(null);
    setFiles((prev) => prev.filter((f) => f.key !== key));
  }

  // The datalist option text: name plus Throxy id, so two clients with the same
  // name (only throxy_client_id is unique) stay distinguishable.
  const clientLabel = (c: AdminClient) => `${c.name} (${c.throxy_client_id})`;

  // Typed text resolves to a client by exact (case-insensitive) label — what the
  // datalist fills in on pick. A bare name also resolves, but only when exactly one
  // client carries it; anything partial, misspelt or ambiguous leaves the file unassigned.
  function setFileClient(key: number, clientQuery: string) {
    const wanted = clientQuery.trim().toLowerCase();
    const byLabel = eligibleClients.filter((c) => clientLabel(c).toLowerCase() === wanted);
    const byName = eligibleClients.filter((c) => c.name.toLowerCase() === wanted);
    const match = byLabel.length === 1 ? byLabel[0] : byName.length === 1 ? byName[0] : null;
    const clientId = match?.id ?? null;
    setFiles((prev) => prev.map((f) => (f.key === key ? { ...f, clientId, clientQuery } : f)));
  }

  // `reuse` is passed explicitly by the duplicate-hint retry (state hasn't
  // re-rendered yet there); the main button uses the current toggle value.
  async function runImport(reuse = reuseExisting) {
    if (!importable.length || !allAssigned || payloadTooBig) return;
    if (!listName.trim()) return;
    setImporting(true);
    setImportError(null);
    try {
      // The summary below only lives inside this modal — toast so the outcome
      // survives a quick "Done".
      const res = await importThroxyList(listName.trim(), importable, reuse);
      setResult(res);
      if (res.pool) {
        const reusedNote = res.reused > 0 ? `, ${res.reused} reused` : '';
        onToast(
          'success',
          `Pool “${res.pool.name}” created — ${res.imported} imported${reusedNote}.`,
        );
      } else {
        onToast('error', 'Import failed — every row was rejected, no pool created.');
      }
      onDone(); // refresh the board so the pool/contacts show up
    } catch (err) {
      setImportError(err instanceof AdminRequestError ? err.message : String(err));
    } finally {
      setImporting(false);
    }
  }

  // The no-CSV path: POST /pools with just the name (pool lands as `draft`, no
  // clients/leads). Nothing to preview, so success closes straight away.
  async function createEmpty() {
    const name = listName.trim();
    if (!name) return;
    setImporting(true);
    setImportError(null);
    try {
      await createPool(name);
      onToast(
        'success',
        `Pool “${name}” created empty — import lists later and move them in via Manage leads.`,
      );
      onDone();
      onClose();
    } catch (err) {
      setImportError(err instanceof AdminRequestError ? err.message : String(err));
    } finally {
      setImporting(false);
    }
  }

  // One field, two homes: right under the intro when creating empty (no file UI
  // has rendered yet), below the parsed file cards on the import path as before.
  const poolNameField = (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">Pool name</span>
      <input
        value={listName}
        onChange={(e) => {
          setNameTouched(true);
          setListName(e.target.value);
        }}
        className="border-input rounded-md border px-2 py-1.5 text-sm"
        placeholder="e.g. L4 Maverick Companies"
      />
    </label>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card flex max-h-[88vh] w-[min(48rem,94vw)] flex-col overflow-hidden rounded-lg border shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-base font-bold">Create pool</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg"
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Everything above the footer scrolls; the footer (confirm + actions) never
            leaves the screen, however long the preview grows. */}
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          {!result && (
            <>
              <p className="text-muted-foreground text-xs">
                Throxy lead exports, one CSV per client. This creates a <b>new pool</b>; files merge
                round-robin so every client dials from the top of its own ranked list. No CSV yet?
                Name the pool and create it empty — when lists arrive, import them here and move
                them in via the card&apos;s <b>Manage leads</b>. Bad rows (no phone /
                do-not-contact) are skipped automatically.
              </p>

              {emptyCreate && poolNameField}

              {files.map((f) => {
                const fileLeads = f.rows ? f.rows.filter((r) => r.skip === null).length : 0;
                return (
                  <div key={f.key} className="flex flex-col gap-2 rounded-md border p-2.5">
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate font-medium">{f.fileName}</span>
                      <div className="flex shrink-0 items-center gap-2">
                        {f.rows && (
                          <span className="text-muted-foreground text-xs">
                            {fileLeads} lead{fileLeads === 1 ? '' : 's'}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeFile(f.key)}
                          className="text-muted-foreground hover:text-foreground"
                          title="Remove file"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    {f.parseError && <p className="text-destructive text-xs">⚠ {f.parseError}</p>}
                    {f.rows && (
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-muted-foreground text-xs">
                          Client — this file&apos;s leads are filed under it
                        </span>
                        {eligibleClients.length > 0 ? (
                          <>
                            <input
                              list={`import-clients-${f.key}`}
                              value={f.clientQuery}
                              onChange={(e) => setFileClient(f.key, e.target.value)}
                              placeholder="Type a client name…"
                              autoComplete="off"
                              className="border-input rounded-md border px-2 py-1.5 text-sm"
                            />
                            <datalist id={`import-clients-${f.key}`}>
                              {eligibleClients.map((c) => (
                                <option key={c.id} value={clientLabel(c)} />
                              ))}
                            </datalist>
                            {f.clientQuery.trim() !== '' && f.clientId == null && (
                              <span className="text-destructive text-xs">
                                No client named &ldquo;{f.clientQuery.trim()}&rdquo; — pick one from
                                the list.
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-destructive text-xs">
                            No client has a Throxy ID yet. Create one with <b>Create client</b>{' '}
                            before importing.
                          </span>
                        )}
                      </label>
                    )}
                  </div>
                );
              })}

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {files.length === 0
                    ? 'Throxy CSV'
                    : `Add another CSV for a multi-client pool${
                        distinctClientCount > 0
                          ? ` (${distinctClientCount} client${distinctClientCount === 1 ? '' : 's'} so far)`
                          : ''
                      }`}
                </span>
                {files.length > 0 && (
                  <span className="text-muted-foreground text-xs">
                    One file per client — add as many clients as you need; everything lands in this
                    one pool.
                  </span>
                )}
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    addFile(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                  className="file:bg-muted text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
                />
              </label>

              {files.some((f) => f.rows) && (
                <>
                  {poolNameField}

                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={reuseExisting}
                      onChange={(e) => setReuseExisting(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium">Re-use leads between pools</span>
                      <span className="text-muted-foreground block text-xs">
                        A lead exists once per client, so some of these may already be in maestro —
                        usually because they sit in another pool. <b>On</b>: link those leads into
                        this pool too, so they belong to both and get dialled here as well (their
                        dial history comes with them). <b>Off</b>: those rows fail and the leads
                        don&rsquo;t land here at all. Either way, leads already in <i>this</i> pool
                        are left untouched and never re-dialled.
                      </span>
                    </span>
                  </label>

                  <div className="flex flex-col gap-2">
                    <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                      <span className="bg-muted rounded-full px-2 py-0.5 font-bold">
                        {importable.length} lead{importable.length === 1 ? '' : 's'} to import
                      </span>
                      {perClientCounts.length > 1 &&
                        perClientCounts.map(([id, count]) => (
                          <span
                            key={id}
                            className="rounded-full bg-sky-100 px-2 py-0.5 font-bold text-sky-800"
                          >
                            {clientName(id) ?? id}: {count}
                          </span>
                        ))}
                      {skipped > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-800">
                          {skipped} skipped
                        </span>
                      )}
                    </div>
                    {combined.contestedCount > 0 && (
                      <p className="text-xs text-amber-700">
                        ⚠ {combined.contestedCount} phone number
                        {combined.contestedCount === 1 ? ' appears' : 's appear'} under more than
                        one client — each is dialed once, split evenly between those clients (hover
                        a ⊘ row to see who kept it). A large overlap usually means the same list was
                        uploaded for two clients.
                      </p>
                    )}
                    {payloadTooBig && (
                      <p className="text-destructive text-xs">
                        ⚠ This import is too large for one request (~
                        {Math.round(payloadBytes / (1024 * 1024))} MB). Split it into separate
                        pools.
                      </p>
                    )}
                    <div className="max-h-72 overflow-y-auto rounded-md border">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                          <tr>
                            <th className="px-2 py-1 font-medium">Name</th>
                            <th className="px-2 py-1 font-medium">Client</th>
                            <th className="px-2 py-1 font-medium">Title</th>
                            <th className="px-2 py-1 font-medium">Company</th>
                            <th className="px-2 py-1 font-medium">Phone</th>
                          </tr>
                        </thead>
                        <tbody>
                          {combined.rows.slice(0, 200).map((r, i) => (
                            <tr
                              key={i}
                              className={cn('border-t', r.skip && 'bg-destructive/5')}
                              title={r.skip ? `Skipped: ${r.skip}` : ''}
                            >
                              <td className="px-2 py-1">
                                {r.skip && <span className="text-destructive mr-1">⊘</span>}
                                {r.contact.name || '—'}
                              </td>
                              <td className="px-2 py-1">{clientName(r.clientId) || '—'}</td>
                              <td className="px-2 py-1">{r.contact.metadata.job_title || '—'}</td>
                              <td className="px-2 py-1">
                                {r.contact.metadata.account_name || '—'}
                              </td>
                              <td className="px-2 py-1 font-mono">{r.contact.phone || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {combined.rows.length > 200 && (
                        <p className="text-muted-foreground px-2 py-1 text-[0.68rem]">
                          …and {combined.rows.length - 200} more (all will be imported).
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {result && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800">
                  {result.imported} imported
                </span>
                {result.reused > 0 && (
                  <span className="rounded-full bg-sky-100 px-2 py-0.5 font-bold text-sky-800">
                    {result.reused} reused
                  </span>
                )}
                {result.numbers_bought > 0 && (
                  <span className="rounded-full bg-violet-100 px-2 py-0.5 font-bold text-violet-800">
                    {result.numbers_bought} caller ID{result.numbers_bought === 1 ? '' : 's'} bought
                  </span>
                )}
                {result.failed > 0 && (
                  <span className="bg-destructive/10 text-destructive rounded-full px-2 py-0.5 font-bold">
                    {result.failed} failed
                  </span>
                )}
                {skipped > 0 && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-800">
                    {skipped} skipped
                  </span>
                )}
              </div>
              {result.pool ? (
                <p className="text-sm">
                  Created pool <b>{result.pool.name}</b> (#{result.pool.id})
                  {result.clients.length > 0 && (
                    <>
                      {' '}
                      for <b>{result.clients.map((c) => c.name).join(', ')}</b>
                    </>
                  )}
                  . Add an SDR and a caller ID, then hit <b>Open</b>.
                </p>
              ) : (
                <p className="text-sm">No pool was created — every row failed.</p>
              )}

              {/* All failures are duplicate-phone rejections and reuse was off — the
                toggle would fix it. Offer a one-click re-import that reuses them. */}
              {allDuplicates && !reuseExisting && (
                <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm">
                  <p className="text-sky-900">
                    These leads already exist for their client — they are elsewhere in maestro,
                    usually in another pool. Turn on <b>Re-use leads between pools</b> to link them
                    into this one as well instead of dropping them.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    disabled={importing}
                    onClick={() => {
                      setReuseExisting(true);
                      setResult(null);
                      void runImport(true);
                    }}
                  >
                    Re-use &amp; import {result.failed} existing
                  </Button>
                </div>
              )}

              {result.failures.length > 0 && (
                <div className="max-h-64 overflow-y-auto rounded-md border">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                      <tr>
                        <th className="px-2 py-1 font-medium">row</th>
                        <th className="px-2 py-1 font-medium">name</th>
                        <th className="px-2 py-1 font-medium">reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.failures.map((f) => (
                        <tr key={f.index} className="border-t">
                          <td className="px-2 py-1 font-mono">{f.index + 1}</td>
                          <td className="px-2 py-1">{f.name || '—'}</td>
                          <td className="text-destructive px-2 py-1">{f.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Fixed footer — always on screen, so the actions never hide below a
            1000-row preview. Post-import: Done. */}
        <div className="flex flex-col gap-3 border-t px-5 py-3">
          {!result ? (
            <>
              {importError && <p className="text-destructive text-xs">⚠ {importError}</p>}

              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={
                    emptyCreate
                      ? !listName.trim() || importing
                      : importable.length === 0 ||
                        !listName.trim() ||
                        !allAssigned ||
                        files.some((f) => f.parseError !== null) ||
                        payloadTooBig ||
                        importing
                  }
                  onClick={() => void (emptyCreate ? createEmpty() : runImport())}
                >
                  {importing
                    ? emptyCreate
                      ? 'Creating…'
                      : 'Importing…'
                    : emptyCreate
                      ? 'Create empty pool'
                      : `Create pool + import ${importable.length || ''} lead${importable.length === 1 ? '' : 's'}`}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex justify-end">
              <Button size="sm" onClick={onClose}>
                Done
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Create a leadflow-syncable client: display name + Throxy client id (both
// required here, though the core accepts an id-less client for the hidden
// plain-CSV path). Surfaces the core's 422 inline (e.g. a duplicate Throxy id).
function CreateClientModal({
  clients,
  onClose,
  onDone,
}: {
  clients: AdminClient[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [throxyClientId, setThroxyClientId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { slug, clash, malformed, ok } = checkThroxyClientId(throxyClientId, clients);
  const canSave = displayName.trim() !== '' && ok && !saving;

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      // Send the normalized slug, not the raw field — the id is lowercase by
      // convention and a stray capital would mint a second, non-matching client.
      await createClient(displayName.trim(), slug);
      onDone(); // refresh the board so the new client shows in the import dropdown
      onClose();
    } catch (err) {
      setError(err instanceof AdminRequestError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card flex w-[min(28rem,94vw)] flex-col gap-4 rounded-lg border p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold">Create client</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg"
            title="Close"
          >
            ✕
          </button>
        </div>

        <p className="text-muted-foreground text-xs">
          The <b>Throxy client ID</b> is leadflow&apos;s match key — imported leads filed under this
          client sync downstream by it.
        </p>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Display name</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="border-input rounded-md border px-2 py-1.5 text-sm"
            placeholder="e.g. Maverick Companies"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Throxy client ID</span>
          <input
            value={throxyClientId}
            onChange={(e) => setThroxyClientId(e.target.value)}
            className="border-input rounded-md border px-2 py-1.5 font-mono text-sm"
            placeholder="e.g. ampup-574d"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
          {clash ? (
            <span className="text-destructive text-xs">
              ⚠ Already used by <b>{clash.name}</b> (client {clash.id}). Import under that client
              instead of creating a second one.
            </span>
          ) : malformed ? (
            <span className="text-destructive text-xs">
              ⚠ Doesn&rsquo;t look like a Throxy org ID — expected lowercase letters, numbers and
              hyphens, like <span className="font-mono">ampup-574d</span>. Paste it from the org
              rather than typing it: it&rsquo;s the key leads sync on.
            </span>
          ) : null}
        </label>

        {error && <p className="text-destructive text-xs">⚠ {error}</p>}

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSave} onClick={() => void save()}>
            {saving ? 'Creating…' : 'Create client'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Pool-card action-row button. The everyday tools are icon-only at rest;
// `hoverLabel` floats the one-word name under the icon on hover (instant, unlike
// the delayed native tooltip, which still carries the full detail via `title`).
// A static `label` caption is reserved for the card's primary action (Open, also
// marked `accent` so it stands out from the routine tools).
function PoolAction({
  label,
  hoverLabel,
  title,
  onClick,
  testId,
  danger,
  accent,
  children,
}: {
  label?: string;
  hoverLabel?: string;
  title: string;
  onClick: () => void;
  testId?: string;
  danger?: boolean;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'text-muted-foreground group relative flex flex-col items-center gap-0.5 rounded px-1 py-0.5 opacity-70 hover:opacity-100',
        danger ? 'hover:bg-destructive/10 hover:text-destructive' : 'hover:bg-muted',
        accent && 'text-emerald-700 opacity-100 hover:bg-emerald-50 hover:text-emerald-800',
      )}
    >
      {children}
      {label && <span className="text-[0.45rem] leading-none">{label}</span>}
      {hoverLabel && (
        <span className="bg-foreground/90 text-background pointer-events-none absolute top-full left-1/2 z-10 mt-0.5 hidden -translate-x-1/2 rounded px-1 py-px text-[0.55rem] leading-tight whitespace-nowrap group-hover:block">
          {hoverLabel}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Left rail — the people who can be SDRs. Sourced from the auth directory (real
// @throxy.com sign-ins) so it's not a hand-maintained list; you ASSIGN them to a
// pool from that pool's SDRs tab, not from here, but removing them
// from everything at once is here, because it's about the person rather than any
// one pool. Two lines per person: who they are and whether they're on a call,
// then what they hold and the one control that takes it all back.
// ---------------------------------------------------------------------------
function SdrRail({
  data,
  onRevoke,
}: {
  data: BoardData;
  onRevoke: (entry: SdrRosterEntry) => void;
}) {
  // Wider than the per-pool assignment this rail offers, so not that permission.
  const canAdmin = useHasPermission('console:admin');
  const live = liveSdrIds(data);
  // Live (currently-calling) SDRs float to the top so an operator sees who's
  // active at a glance; a stable sort keeps everyone else in their directory
  // (alphabetical) order.
  const roster = buildSdrRoster(data.directory, data.sdrs).sort((a, b) => {
    const aLive = a.sdrId != null && live.has(a.sdrId);
    const bLive = b.sdrId != null && live.has(b.sdrId);
    return Number(bLive) - Number(aLive);
  });
  const fromDirectory = data.directory.length > 0;
  const liveCount = roster.filter((e) => e.sdrId != null && live.has(e.sdrId)).length;

  return (
    <aside className="bg-muted/30 w-60 flex-none rounded-lg border p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold">All SDRs</span>
        <div className="flex items-center gap-1">
          {liveCount > 0 && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 text-xs font-bold text-emerald-700"
              title={`${liveCount} SDR${liveCount === 1 ? '' : 's'} on a call now`}
            >
              <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-600" />
              {liveCount}
            </span>
          )}
          <span
            className="bg-muted text-muted-foreground rounded-full px-2 text-xs font-bold"
            title="Total signed-in SDRs"
          >
            {roster.length}
          </span>
        </div>
      </div>
      <p className="text-muted-foreground mt-1 text-[0.68rem] leading-snug">
        {fromDirectory
          ? 'Signed-in @throxy.com people. Assign them from a pool’s “Assign SDRs”.'
          : 'Dev SDRs (auth off). Assign them from a pool’s “Assign SDRs”.'}
      </p>
      <div className="mt-3 flex max-h-[520px] flex-col gap-1.5 overflow-y-auto">
        {roster.length === 0 ? (
          <p className="text-muted-foreground py-2 text-center text-xs italic">No SDRs yet.</p>
        ) : (
          roster.map((e) => {
            const isLive = e.sdrId != null && live.has(e.sdrId);
            return (
              <div
                key={e.key}
                className={cn(
                  'bg-card rounded-md border px-2 py-1.5 text-sm',
                  isLive && 'border-l-[3px] border-l-emerald-600 bg-emerald-50',
                )}
                title={e.email ?? e.name}
              >
                <div className="flex items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate font-medium">{e.name}</span>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-1.5 text-[0.65rem]',
                      isLive ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {isLive ? 'calling' : 'idle'}
                  </span>
                </div>
                <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-[0.65rem]">
                  <span className="flex-1 tabular-nums">
                    {e.pool_ids.length} pool{e.pool_ids.length === 1 ? '' : 's'}
                  </span>
                  {e.sdrId != null && canAdmin && (
                    <button
                      type="button"
                      className="hover:text-destructive shrink-0"
                      onClick={() => onRevoke(e)}
                      aria-label={`Remove ${e.name} from every pool`}
                      title="Drop every pool assignment and release the seat they're on"
                    >
                      <UserMinus className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Center — one column per pool.
// ---------------------------------------------------------------------------

// Filter/sort options as typed tables rather than literal <option>s: run_state is
// a plain string on the wire, so a typo'd value would compile and silently match
// nothing. Live states first — those are the ones an operator acts on.
const POOL_STATE_OPTIONS: readonly { value: PoolStateFilter; label: string }[] = [
  { value: 'all', label: 'All states' },
  { value: 'calling', label: 'Calling' },
  { value: 'open', label: 'Open' },
  { value: 'paused', label: 'Paused' },
  { value: 'draining', label: 'Draining' },
  { value: 'draft', label: 'Draft' },
  { value: 'closed', label: 'Closed' },
];

// "Sort:" is carried in the labels because the filter row has no field labels —
// without it a bare "Name (A–Z)" reads as another filter.
const POOL_SORT_OPTIONS: readonly { value: PoolSort; label: string }[] = [
  { value: 'active', label: 'Sort: Active first' },
  { value: 'newest', label: 'Sort: Newest' },
  { value: 'name', label: 'Sort: Name (A–Z)' },
  { value: 'undialled', label: 'Sort: Undialled leads' },
];

function PoolColumns({
  data,
  version,
  dragRef,
  onDeletePool,
  onClonePool,
  onToggleHidden,
  onToggleOpenToAll,
  onUpdatePool,
  onRenamePool,
  onSavePool,
  onChanged,
}: {
  data: BoardData;
  dragRef: React.RefObject<DragPayload | null>;
  onDeletePool: (id: number, name: string) => void;
  onClonePool: (id: number) => void;
  onToggleHidden: (id: number, hidden: boolean) => void;
  onToggleOpenToAll: (id: number, open: boolean) => void;
  onUpdatePool: (id: number, patch: PoolConfigPatch) => void;
  // Resolves to an error message to render inline, or null on success.
  onRenamePool: (id: number, name: string) => Promise<string | null>;
  onSavePool: (id: number, patch: PoolConfigPatch) => Promise<string | null>;
  onChanged: () => void;
  // Bumped per load, so a column that failed on data since replaced renders again.
  version: number;
}) {
  const live = liveSdrIds(data);
  const [query, setQuery] = useState('');
  // Default 'all': new pools are born as drafts, so an 'active' default would
  // hide them right after creation.
  const [stateFilter, setStateFilter] = useState<PoolStateFilter>('all');
  const [sdrKey, setSdrKey] = useState<string | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [sort, setSort] = useState<PoolSort>('active');

  if (data.pools.length === 0) {
    return (
      <div className="text-muted-foreground flex-1 rounded-lg border border-dashed p-10 text-center text-sm">
        No pools yet. <b className="text-foreground">Create pool</b> above creates one from your
        lead export (or empty, to fill later), then assign SDRs to start dialing.
      </div>
    );
  }

  // SDR dropdown is sourced from the FULL roster (assigned people, even off-hours),
  // sorted by name; a pool matches if the selection is assigned OR live-calling it.
  const roster = buildSdrRoster(data.directory, data.sdrs);
  const sdrOptions = [...roster].sort((a, b) => a.name.localeCompare(b.name));
  // Only clients that own a pool (their effective client), by name — so the filter
  // has no zero-result options.
  const clientOptions = [
    ...new Map(
      data.pools
        .map((p) => p.effective_client)
        .filter((c): c is NonNullable<typeof c> => c != null)
        .map((c) => [c.id, c] as const),
    ).values(),
  ].sort((a, b) => a.name.localeCompare(b.name));
  const visible = sortPools(
    filterPools(
      data.pools,
      { query, state: stateFilter, sdrKey, client: clientId },
      { roster, active: data.active },
    ),
    sort,
    data.active, // live SDRs promote a pool to the top "calling" tier
  );
  const clearFilters = () => {
    setQuery('');
    setStateFilter('all');
    setSdrKey(null);
    setClientId(null);
    setSort('active');
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="flex items-center gap-2 px-1">
        <span className="text-sm font-bold">Pools</span>
        <span className="bg-muted text-muted-foreground rounded-full px-2 text-xs font-bold">
          {visible.length === data.pools.length
            ? data.pools.length
            : `${visible.length} / ${data.pools.length}`}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 px-1">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search pools…"
          className="border-input rounded-md border px-2 py-1.5 text-sm"
        />
        <select
          value={clientId ?? ''}
          onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : null)}
          className="rounded border px-2 py-1 text-xs"
        >
          <option value="">All clients</option>
          {clientOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value as PoolStateFilter)}
          className="rounded border px-2 py-1 text-xs"
        >
          {POOL_STATE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={sdrKey ?? ''}
          onChange={(e) => setSdrKey(e.target.value || null)}
          className="rounded border px-2 py-1 text-xs"
        >
          <option value="">All SDRs</option>
          {sdrOptions.map((e) => (
            <option key={e.key} value={e.key}>
              {e.name}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as PoolSort)}
          className="rounded border px-2 py-1 text-xs"
          title="Undialled ≈ contacts minus those dialed at least once (lifetime); contacts removed after being dialed understate it"
        >
          {POOL_SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {visible.length === 0 ? (
        <div className="text-muted-foreground flex-1 rounded-lg border border-dashed p-10 text-center text-sm">
          No pools match your filters.{' '}
          <button
            type="button"
            className="text-foreground font-medium underline"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-3 pb-2">
          {visible.map((pool) => (
            // Per column, so one pool whose data will not render costs that
            // pool rather than every other one on the board. A failed column
            // offers no drop target, which is better than a drop whose result
            // cannot be drawn.
            <Boundary
              key={pool.id}
              where="region:board-pool-column"
              resetKey={`${pool.id}:${version}`}
              size="panel"
            >
              <PoolColumn
                pool={pool}
                data={data}
                live={live}
                dragRef={dragRef}
                onDeletePool={onDeletePool}
                onClonePool={onClonePool}
                onToggleHidden={onToggleHidden}
                onToggleOpenToAll={onToggleOpenToAll}
                onUpdatePool={onUpdatePool}
                onRenamePool={onRenamePool}
                onSavePool={onSavePool}
                onChanged={onChanged}
              />
            </Boundary>
          ))}
        </div>
      )}
    </div>
  );
}

// Pool lifecycle badge (draft/open/calling/paused/draining/closed). Colored so an
// operator spots non-live pools at a glance; `open`/`calling` are the live joinable
// states (`calling` = SDRs actively dialing). State is system-driven (the dialer
// sets open/calling/paused/draining/closed); the only operator move is draft → open
// via the "Open" button.
const POOL_STATE_BADGE: Record<string, string> = {
  open: 'bg-green-600',
  calling: 'bg-emerald-500',
  paused: 'bg-amber-500',
  draining: 'bg-blue-600',
  draft: 'bg-gray-500',
  closed: 'bg-gray-700',
};
function PoolStateBadge({ state }: { state: string }) {
  return (
    <span
      className={cn(
        'mt-0.5 shrink-0 rounded-md px-1.5 py-px text-[0.65rem] font-bold text-white',
        POOL_STATE_BADGE[state] ?? 'bg-gray-500',
      )}
      title={`Lifecycle state: ${state} (SDRs can join only open/calling/paused pools)`}
    >
      {state}
    </span>
  );
}

// Confirm for a client chip dropped on another pool. Deliberately typed-free but
// explicit: the button carries the count and both pool names, because "move a client"
// is a bigger act than anything else reachable by dragging on this board.
function MoveClientModal({
  drag,
  target,
  onClose,
  onDone,
}: {
  drag: ClientDrag;
  target: AdminPool;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [explain, setExplain] = useState<string | null>(null);
  // Dragging the whole chip means the whole client, so that is what it opens on.
  const [stage, setStage] = useState<'any' | 'to_dial' | 'dialled_here'>('any');

  const apply = async (stage: 'any' | 'to_dial' | 'dialled_here') => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await moveLeads(
        drag.fromPoolId,
        { client_id: drag.id, kind: 'both', stage, count: 'all' },
        target.id,
      );
      onDone();
    } catch (e) {
      setErr(e instanceof AdminRequestError ? e.message : 'Move failed.');
      setBusy(false);
    }
  };

  const dialled = Math.max(0, drag.count - drag.undialed);

  // Pick a slice, then one Move — same shape as the Manage-leads selector, so the two
  // ways into a move behave the same. The reasoning sits behind a "?" per option: each
  // needs a different one, and inline that is three paragraphs about choices the reader
  // has not made yet.
  const options = (
    [
      {
        stage: 'any' as const,
        label: 'All leads',
        n: drag.count,
        why: `Everything this client has here. The already-dialled keep that state, so ${target.name} will not ring them until you open Redial there.`,
      },
      {
        stage: 'to_dial' as const,
        label: 'Undialled leads',
        n: drag.undialed,
        why: `Only what this pool has not rung. They join ${target.name}'s queue and get dialled there.`,
      },
      {
        stage: 'dialled_here' as const,
        label: 'Already dialled',
        n: dialled,
        why: `Only what this pool has rung. They arrive still marked dialled — open Redial in ${target.name} to ring them again.`,
      },
    ] as const
  ).filter(
    // The halves are only a choice when there ARE two halves. With either at zero, one
    // of them equals "All leads" and the list shows the same number twice — which reads
    // as a bug, not an option.
    (o) => o.stage === 'any' || (drag.undialed > 0 && dialled > 0),
  );

  // A stage the filter removed must not stay selected — it would leave nothing checked
  // and a Move button quoting a count from a row that is not on screen.
  const chosen = options.find((o) => o.stage === stage)?.n ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-card flex w-full max-w-md flex-col gap-3 rounded-lg border p-4 shadow-xl">
        <b className="text-base">
          Move{' '}
          <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[0.8em] font-bold text-sky-800">
            {drag.name}
          </span>{' '}
          into {target.name}
        </b>

        <table className="w-full text-sm">
          <tbody>
            {options.map((o) => (
              <Fragment key={o.stage}>
                <tr
                  className={`hover:bg-muted/50 cursor-pointer border-t ${
                    o.n === 0 ? 'opacity-40' : ''
                  }`}
                  onClick={() => o.n > 0 && setStage(o.stage)}
                  data-testid={`move-client-${o.stage}`}
                >
                  <td className="py-2.5 ps-2">
                    <input
                      type="radio"
                      checked={stage === o.stage}
                      disabled={o.n === 0}
                      onChange={() => setStage(o.stage)}
                    />
                  </td>
                  <td className="w-px pe-5 ps-3 text-right text-base font-semibold tabular-nums">
                    {o.n.toLocaleString()}
                  </td>
                  <td className="w-full">{o.label}</td>
                  <td className="w-px pe-2 ps-2 text-right">
                    <button
                      type="button"
                      aria-label={`What does ${o.label} mean?`}
                      onClick={(e) => {
                        // Its own control: reading about an option is not choosing it.
                        e.stopPropagation();
                        setExplain(explain === o.stage ? null : o.stage);
                      }}
                      className="text-muted-foreground hover:bg-muted hover:text-foreground h-5 w-5 rounded-full border text-xs"
                    >
                      ?
                    </button>
                  </td>
                </tr>
                {explain === o.stage && (
                  <tr>
                    <td />
                    <td colSpan={3} className="text-muted-foreground pb-1.5 text-xs">
                      {o.why}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>

        {(stage === 'dialled_here' || (stage === 'any' && dialled > 0)) && (
          <p className="rounded-md border border-sky-300 bg-sky-50 p-2 text-xs text-sky-900">
            {dialled.toLocaleString()} of these are already dialled and arrive still marked that
            way. Open <b>Redial</b> in {target.name} afterwards to ring them.
          </p>
        )}

        {err && (
          <p className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900">
            {err}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={busy || chosen === 0}
            onClick={() => void apply(stage)}
            data-testid="move-client-apply"
          >
            {busy ? 'Moving…' : `Move ${chosen.toLocaleString()}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// The pool's name, edited where it is read. A rename is a correction far more often
// than it is a decision, and a modal made a two-word fix a four-step errand.
//
// Enter commits; Escape and clicking away revert. Committing is the only path that
// writes, so a stray click on a card can never rename a pool. The local checks mirror
// the core's, and its 422 lands in the same place they do.
function PoolNameField({
  pool,
  otherNames,
  onRename,
}: {
  pool: AdminPool;
  otherNames: string[];
  // Resolves to an error message to render inline, or null on success.
  onRename: (id: number, name: string) => Promise<string | null>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(pool.name);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const canAdmin = useHasPermission('console:admin');

  const stop = () => {
    setEditing(false);
    setErr(null);
  };

  async function commit() {
    const local = validatePoolName(draft, pool.name, otherNames);
    if (local) return setErr(local);
    if (draft.trim() === pool.name) return stop();

    setErr(null);
    setSaving(true);
    const failure = await onRename(pool.id, draft.trim());
    setSaving(false);
    // Disabling the input to save can itself fire blur in some browsers, which would
    // have exited edit mode — so re-enter it rather than dropping the error into a
    // branch that is no longer rendered.
    if (failure) {
      setEditing(true);
      setErr(failure);
    } else stop();
  }

  // A disabled button takes no hover and surfaces no tooltip, so it would read as a
  // dead control rather than a read-only one.
  if (!canAdmin)
    return (
      <span className="min-w-0 flex-1 text-left text-[0.9375rem] font-bold break-words">
        {pool.name}
      </span>
    );

  if (!editing)
    return (
      <button
        type="button"
        onClick={() => {
          // Re-read the name at open: the board polls, so a rename from another
          // session may have landed since this card last rendered.
          setDraft(pool.name);
          setEditing(true);
        }}
        data-testid="rename-pool"
        title="Click to rename"
        className="hover:bg-muted min-w-0 flex-1 rounded text-left text-[0.9375rem] font-bold break-words"
      >
        {pool.name}
      </button>
    );

  return (
    <div className="min-w-0 flex-1">
      <input
        autoFocus
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (!saving) stop();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void commit();
          if (e.key === 'Escape') stop();
        }}
        data-testid="rename-pool-input"
        title="Enter to rename · Escape to cancel"
        className="w-full rounded border px-1 py-0.5 text-[0.9375rem] font-bold"
      />
      {err && <p className="text-destructive mt-0.5 text-[0.65rem]">⚠ {err}</p>}
    </div>
  );
}

function PoolColumn({
  pool,
  data,
  live,
  dragRef,
  onDeletePool,
  onClonePool,
  onToggleHidden,
  onToggleOpenToAll,
  onUpdatePool,
  onRenamePool,
  onSavePool,
  onChanged,
}: {
  pool: AdminPool;
  data: BoardData;
  live: Set<number>;
  dragRef: React.RefObject<DragPayload | null>;
  onDeletePool: (id: number, name: string) => void;
  onClonePool: (id: number) => void;
  onToggleHidden: (id: number, hidden: boolean) => void;
  onToggleOpenToAll: (id: number, open: boolean) => void;
  onUpdatePool: (id: number, patch: PoolConfigPatch) => void;
  // Resolves to an error message to render inline, or null on success.
  onRenamePool: (id: number, name: string) => Promise<string | null>;
  onSavePool: (id: number, patch: PoolConfigPatch) => Promise<string | null>;
  onChanged: () => void;
}) {
  // Whether the details panel is open over this card.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const closeDetails = useCallback(() => setDetailsOpen(false), []);
  const canAdmin = useHasPermission('console:admin');
  const canOpenToAll = useHasPermission('pool-open-to-all:write');
  // Non-null while the draft→Open gate blocked the transition — holds the list of
  // unmet requirements to show in the modal.
  const [openBlocked, setOpenBlocked] = useState<string[] | null>(null);
  const color = safeColor(pool.color);
  // Assigned roster (real people, from the auth directory) vs who's live on a call
  // right now (runtime `active` map, resolved to names via the core SDR rows).
  const roster = buildSdrRoster(data.directory, data.sdrs);
  const colSdrs = assignedRosterInPool(roster, pool.id);
  const callingSdrs = callingSdrsInPool(data.sdrs, data.active, pool.id);
  const contactCount = pool.contact_count;
  const liveCount = callingSdrs.length;
  const dialableWhenClosed = closedPoolDialable(pool);

  // Every number we own — the pool's candidate set IS the inventory.
  const callerNums = data.numbers;

  // A pool can go from draft → Open only once it can actually dial someone: it needs
  // a caller ID to dial FROM, an SDR to take the calls, and a contact to dial TO.
  // Anything missing blocks the transition and lists what to set (the modal below).
  const openMissing = poolOpenBlockers({
    callerIdCount: callerNums.length,
    assignedSdrCount: colSdrs.length,
    contactCount,
  });

  // A client chip dragged from another card. Held until confirmed — moving a client's
  // whole presence is not something a slip of the mouse should do.
  const [clientDrop, setClientDrop] = useState<ClientDrag | null>(null);
  const [dropOver, setDropOver] = useState(false);

  return (
    <div
      onDragOver={(e) => {
        const p = dragRef.current;
        // Its own pool is not a destination, and the other drag kinds have their own
        // zones inside the card.
        if (p?.kind !== 'client' || p.fromPoolId === pool.id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropOver(true);
      }}
      onDragLeave={() => setDropOver(false)}
      onDrop={(e) => {
        const p = dragRef.current;
        setDropOver(false);
        if (p?.kind !== 'client' || p.fromPoolId === pool.id) return;
        e.preventDefault();
        e.stopPropagation();
        setClientDrop(p);
      }}
      className={cn(
        'bg-card flex max-h-[640px] w-64 flex-none flex-col overflow-hidden rounded-lg border',
        pool.hidden && 'opacity-60',
        dropOver && 'ring-2 ring-blue-500 ring-offset-1',
      )}
    >
      {/* The top edge doubles as a live signal: the pool's colour normally, green
          while calls are actually happening (dialer in `calling`, or an SDR live
          on a call in this pool). */}
      <div
        className="border-b p-3"
        style={{
          borderTop: `4px solid ${pool.run_state === 'calling' || liveCount > 0 ? '#10b981' : color}`,
        }}
      >
        {/* Identity on one row: which pool this is, what it's called (editable in
            place — the name is the thing operators correct most often), and what it
            is doing right now. */}
        <div className="flex flex-wrap items-start gap-2">
          <span className="text-muted-foreground shrink-0 pt-0.5 font-mono text-xs font-bold">
            #{pool.id}
          </span>
          <PoolNameField
            pool={pool}
            otherNames={data.pools.filter((p) => p.id !== pool.id).map((p) => p.name)}
            onRename={onRenamePool}
          />
          <PoolStateBadge state={pool.run_state} />
          {/* Otherwise a closed pool holding work reads exactly like a spent one. */}
          {dialableWhenClosed !== null && dialableWhenClosed > 0 && (
            <span
              className="mt-0.5 shrink-0 rounded-md bg-sky-600 px-1.5 py-px text-[0.65rem] font-bold text-white"
              title={`${dialableWhenClosed.toLocaleString()} leads are dialable right now, this pool is closed. Leads asleep in their own timezone are not counted.`}
            >
              {dialableWhenClosed.toLocaleString()} dialable
            </span>
          )}
          {pool.hidden && (
            <span className="mt-0.5 shrink-0 rounded-full bg-gray-500 px-1.5 text-[0.65rem] font-bold text-white">
              hidden
            </span>
          )}
          {pool.open_to_all && (
            <span
              className="mt-0.5 shrink-0 rounded-full bg-amber-600 px-1.5 text-[0.65rem] font-bold text-white"
              title="Any SDR can join this pool, not only the ones assigned to it"
            >
              open to all
            </span>
          )}
        </div>
        {/* The pool's client mix (contact-derived — a pool can span several clients),
            each with its still-to-dial share of the pool total. Same Postgres
            `dialed_at` basis as the progress block below.
            Capped at ~5 rows and scrolled past that: the card is `overflow-hidden`
            at 640px, so a pool spanning dozens of clients would otherwise push
            the action row below off the card entirely. */}
        {(pool.clients?.length ?? 0) > 0 && (
          <div
            className="mt-1 flex max-h-28 flex-wrap gap-1 overflow-y-auto"
            data-testid="pool-clients"
          >
            {pool.clients?.map((c) => {
              // `?? c.count` keeps the chip sane in the deploy window where a newer SPA
              // reads a core that doesn't send `undialed` yet (treat all as to-dial).
              // What this client could be dialed for right now, over what it can ever be
              // dialed for. Falls back to the membership counts on an older core, which
              // reads high rather than absent.
              const toDial = c.available_now ?? c.undialed ?? c.count;
              const ofTotal = c.total_available ?? c.count;
              const name = c.name ?? `client ${c.id}`;
              return (
                <span
                  key={c.id}
                  // Drag the chip onto another pool to move this client's WHOLE presence
                  // — dialled and not — rather than opening Manage leads and picking a
                  // slice. The chip already means "this client, in this pool" everywhere
                  // else, so dragging it is the same idea made physical.
                  draggable={canAdmin}
                  onDragStart={(e) => {
                    const payload: DragPayload = {
                      kind: 'client',
                      id: c.id,
                      name,
                      fromPoolId: pool.id,
                      count: c.count,
                      undialed: toDial,
                    };
                    dragRef.current = payload;
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', name);
                  }}
                  onDragEnd={() => {
                    dragRef.current = null;
                  }}
                  className={cn(
                    'rounded-full bg-sky-100 px-1.5 py-0.5 text-[0.65rem] font-bold text-sky-800',
                    canAdmin && 'cursor-grab active:cursor-grabbing',
                  )}
                  title={`${name}: ${toDial} dialable right now, of ${ofTotal} this pool can ever dial for them. Leads waiting on a calling window or their ring ladder are not counted.${canAdmin ? ` Drag onto another pool to move all ${c.count} of this client's leads, dialable or not.` : ''}`}
                >
                  {name} · {toDial}/{ofTotal} to dial
                </span>
              );
            })}
          </div>
        )}
        {/* Only the controls an operator reaches for while dialing is happening.
            Everything that reads rather than acts is one click away in Details.
            (Delete and Clone are intentionally not surfaced; their onDeletePool /
            onClonePool handlers stay threaded so each is a one-line restore.) */}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-1 gap-y-1">
          {/* Open — the ONE operator state transition (draft → open, lets SDRs join
              and the dialer start; every other state is system-driven). Lives here
              with the other actions so the state badge stays a pure status. */}
          {pool.run_state === 'draft' && canAdmin && (
            <PoolAction
              label="Open"
              accent
              title="Open this pool — lets SDRs join and the dialer start. (The only manual state change; the rest are automatic.)"
              onClick={() =>
                openMissing.length
                  ? setOpenBlocked(openMissing)
                  : onUpdatePool(pool.id, { run_state: 'open' })
              }
              testId="pool-open"
            >
              <Play className="h-3.5 w-3.5" />
            </PoolAction>
          )}
          {/* Pause. Shown only while the pool is actually dialing (open/calling) — a
              draft/draining/closed or already-paused pool has nothing to pause.
              Pausing stops new dials (SDRs stay joined, in-flight calls finish) and
              sets the badge to `paused`. There is NO resume button: the pause isn't
              sticky — the pool comes back to `calling` on its own the moment an SDR
              joins (see auto_resume_on_join in the core). */}
          {['open', 'calling'].includes(pool.run_state) && canAdmin && (
            <PoolAction
              hoverLabel="Pause"
              title="Pause dialing — stops new dials (SDRs stay joined, in-flight calls finish) and sets the pool to paused. It resumes automatically when an SDR joins."
              onClick={() => onUpdatePool(pool.id, { operator_paused: true })}
              testId="pool-pause"
            >
              <Pause className="h-3.5 w-3.5" />
            </PoolAction>
          )}
          {canAdmin && (
            <PoolAction
              hoverLabel={pool.hidden ? 'Show' : 'Hide'}
              title={
                pool.hidden ? 'Show this pool in the cockpit' : 'Hide this pool from the cockpit'
              }
              onClick={() => onToggleHidden(pool.id, !pool.hidden)}
            >
              {pool.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </PoolAction>
          )}
          {canOpenToAll && (
            <PoolAction
              hoverLabel={pool.open_to_all ? 'Assigned only' : 'Open to all'}
              title={
                pool.open_to_all
                  ? 'Return this pool to its assigned SDRs — anyone who was only here because it was open goes back to the picker once their call ends'
                  : 'Let any SDR join this pool, not just the ones assigned to it'
              }
              onClick={() => onToggleOpenToAll(pool.id, !pool.open_to_all)}
            >
              {pool.open_to_all ? (
                <DoorOpen className="h-3.5 w-3.5" />
              ) : (
                <DoorClosed className="h-3.5 w-3.5" />
              )}
            </PoolAction>
          )}
          <PoolAction
            hoverLabel="Details"
            title="Everything about this pool: outcomes, availability, events, dispositions, recordings, leads, SDRs, numbers and its dialing settings"
            onClick={() => setDetailsOpen(true)}
            testId="pool-details"
          >
            <PanelsTopLeft className="h-3.5 w-3.5" />
          </PoolAction>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-2.5">
        {/* Live operational view: who's actually in the pool right now (joined via
            the cockpit — idle, on a call, or wrapping up), from the polled runtime
            `active` map. Distinct from the assigned roster below.
            Omitted entirely when nobody is present, so "Calling now" is a
            find-in-page marker for the pools that actually have people in them. */}
        {callingSdrs.length > 0 && (
          <div>
            <div className="text-muted-foreground mb-1 text-[0.68rem] font-bold uppercase">
              Calling now
            </div>
            <div className="flex flex-wrap gap-1.5">
              {callingSdrs.map((s) => (
                <span
                  key={s.id}
                  className="flex items-center gap-1 rounded-full border border-emerald-600 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800"
                  title={s.email ?? undefined}
                >
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-600" />
                  {s.name || `#${s.id}`}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* Who created this pool + when (from the signed-in operator at create/
            import/clone time). A separated footer so it reads as card metadata,
            not another action. Hidden for pools that predate created-by tracking
            or were made outside an authenticated session (local/CI, null). */}
        {pool.created_by ? (
          <div className="mt-auto border-t pt-2">
            <div className="text-muted-foreground mb-1 text-[0.68rem] font-bold uppercase">
              Created by
            </div>
            <div
              className="flex items-center gap-1.5 text-xs"
              title={`${pool.created_by} · ${formatCreatedAt(pool.created_at)}`}
            >
              <User className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
              <span className="truncate font-medium">{pool.created_by}</span>
            </div>
            <div className="text-muted-foreground mt-1 flex items-center gap-1.5 text-[0.7rem]">
              <Clock className="h-3 w-3 shrink-0" />
              {formatCreatedAt(pool.created_at)}
            </div>
          </div>
        ) : null}
      </div>

      {/* Portalled to <body> so the card's `pool.hidden` opacity-60 (CSS opacity
          applies to the whole subtree — `fixed` doesn't escape it) and its
          `overflow-hidden` don't dim or clip these full-screen overlays. */}
      {detailsOpen &&
        createPortal(
          <PoolDetailsPanel
            pool={pool}
            data={data}
            live={live}
            onClose={closeDetails}
            onChanged={onChanged}
            onSavePool={onSavePool}
          />,
          document.body,
        )}

      {clientDrop &&
        createPortal(
          <MoveClientModal
            drag={clientDrop}
            target={pool}
            onClose={() => setClientDrop(null)}
            onDone={() => {
              setClientDrop(null);
              onChanged();
            }}
          />,
          document.body,
        )}

      {openBlocked &&
        createPortal(
          <PoolOpenBlockedModal
            pool={pool}
            missing={openBlocked}
            onClose={() => setOpenBlocked(null)}
          />,
          document.body,
        )}
    </div>
  );
}

// Friendly local date + time for a pool's creation footer, e.g. "13 Jul 2026,
// 15:42" (locale-formatted). Coarser than formatEventTime — no seconds/micros,
// since a human-readable "when was this made" is all the footer needs. Guards a
// bad/absent timestamp so the footer never renders "Invalid Date".
function formatCreatedAt(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PoolOpenBlockedModal({
  pool,
  missing,
  onClose,
}: {
  pool: AdminPool;
  missing: string[];
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card flex w-[min(28rem,92vw)] flex-col gap-3 rounded-lg border p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold">
            Can’t open <span className="font-mono">{pool.name}</span> yet
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg"
            title="Close"
          >
            ✕
          </button>
        </div>
        <p className="text-muted-foreground text-sm">
          Before this pool can go live and start dialing, set:
        </p>
        <ul className="flex flex-col gap-1.5">
          {missing.map((m) => (
            <li key={m} className="flex items-start gap-2 text-sm">
              <span className="text-destructive mt-0.5 shrink-0">▲</span>
              <span>{m}</span>
            </li>
          ))}
        </ul>
        <div className="mt-1 flex justify-end">
          <Button size="sm" onClick={onClose}>
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
}
